import { observer } from "mobx-react-lite";
import { useId, useState, type ChangeEvent, type ReactNode } from "react";
import { ApiRequestError } from "../../platform/api-client";
import { Button } from "../../ui/button";
import { Dialog } from "../../ui/dialog";
import { Field } from "../../ui/field";
import { Form } from "../../ui/form";
import { TextInput } from "../../ui/text-input";
import { useAppStores } from "../app-context";
import type { MessageKey } from "../locale-store";
import type { SelectableOrganizationView } from "../session-store";
import { workContextLabel } from "../work-context-labels";

/** Combines a grant and one of its organizations into one selectable option. */
function optionKey(grantId: string, organizationUnitId: string): string {
  return `${grantId}::${organizationUnitId}`;
}

function isGlobalAdministrator(roleCode: string): boolean {
  return roleCode === "superuser" ||
    roleCode === "super_admin" ||
    roleCode === "access_administrator";
}

/**
 * Header identity + AUTH-021/multi-OV work-context selector + sign-out.
 *
 * Only rendered inside the authenticated shell. Selecting a context narrows
 * authority to the selected grant AND organization and re-narrows the
 * discoverable feature set; the note is associated with the control so the
 * narrowing semantics are announced, not implied. Never widens authority: the
 * selector only ever shows organizations already present in an owned grant's
 * roots, or descendants of them via the search dialog.
 */
interface SessionControlsProps {
  readonly hideIdentity?: boolean;
}

export const SessionControls = observer(function SessionControls({
  hideIdentity = false,
}: SessionControlsProps): ReactNode {
  const { api, locale, session } = useAppStores();
  const noteId = useId();
  const [error, setError] = useState<MessageKey | null>(null);
  const [browseGrantId, setBrowseGrantId] = useState<string | null>(null);
  const [browseQuery, setBrowseQuery] = useState("");
  const [browseResults, setBrowseResults] = useState<readonly SelectableOrganizationView[] | null>(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<MessageKey | null>(null);

  const contexts = session.availableContexts;
  const directOptions = contexts.flatMap((context) =>
    context.organizations.map((organization) => {
      const organizationLabel =
        isGlobalAdministrator(context.roleCode) &&
          context.organizationScopeUnitIds.includes(organization.unitId)
          ? locale.code === "de" ? "Alle Organisationen" : "All organizations"
          : organization.label;
      return {
        grantId: context.grantId,
        key: optionKey(context.grantId, organization.unitId),
        label: `${organizationLabel} — ${workContextLabel(context.roleCode, locale.code)}`,
        organizationLabel,
        organizationUnitId: organization.unitId,
        roleCode: context.roleCode,
      };
    }),
  );
  const activeContextOption =
    session.activeContext !== null &&
      !directOptions.some((option) =>
        option.grantId === session.activeContext?.grantId &&
        option.organizationUnitId === session.activeContext.organizationUnit.id)
      ? {
          grantId: session.activeContext.grantId,
          key: optionKey(
            session.activeContext.grantId,
            session.activeContext.organizationUnit.id,
          ),
          label: `${session.activeContext.organizationUnit.name ?? (locale.code === "de" ? "Organisation nicht verfügbar" : "Organization unavailable")} — ${workContextLabel(session.activeContext.roleCode, locale.code)}`,
          organizationLabel: session.activeContext.organizationUnit.name ?? (locale.code === "de" ? "Organisation nicht verfügbar" : "Organization unavailable"),
          organizationUnitId: session.activeContext.organizationUnit.id,
          roleCode: session.activeContext.roleCode,
        }
      : null;
  const visibleOptions =
    activeContextOption === null
      ? directOptions
      : [activeContextOption, ...directOptions];
  // Root-scoped Super Admin stays on "All organizations" by default but can
  // still browse into a specific regional or local unit.
  const browsableContexts = contexts.filter((context) =>
    context.organizations.length === 0 ||
    isGlobalAdministrator(context.roleCode));
  const activeKey =
    session.activeContext === null
      ? ""
      : optionKey(session.activeContext.grantId, session.activeContext.organizationUnit.id);

  async function performSwitch(grantId: string, organizationUnitId: string): Promise<void> {
    setError(null);
    try {
      await session.switchContext(api, grantId, organizationUnitId);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.status === 412) {
        setError("workContextStaleVersion");
      } else if (caught instanceof ApiRequestError && caught.status === 403) {
        setError("workContextRevoked");
      } else {
        setError("workContextSwitchError");
      }
    }
  }

  async function onSwitch(key: string): Promise<void> {
    if (key.length === 0 || key === activeKey) return;
    const option = directOptions.find((candidate) => candidate.key === key);
    if (option === undefined) return;
    await performSwitch(option.grantId, option.organizationUnitId);
  }

  async function onLogout(): Promise<void> {
    setError(null);
    try {
      await session.logout(api);
    } catch {
      setError("logoutError");
    }
  }

  async function onChangePassword(): Promise<void> {
    if (newPassword !== newPasswordConfirmation) {
      setPasswordError("changePasswordMismatch");
      return;
    }
    setPasswordBusy(true);
    setPasswordError(null);
    try {
      await session.changePassword(api, currentPassword, newPassword);
    } catch (caught) {
      setPasswordError(
        caught instanceof ApiRequestError &&
          caught.problem?.code === "INVALID_CREDENTIALS"
          ? "changePasswordInvalidCurrent"
          : "changePasswordFailed",
      );
    } finally {
      setPasswordBusy(false);
    }
  }

  async function runBrowse(grantId: string, query: string): Promise<void> {
    setBrowseLoading(true);
    setBrowseError(false);
    try {
      const result = await session.loadSelectableOrganizations(
        api,
        grantId,
        query.length > 0 ? { limit: 50, q: query } : { limit: 50 },
      );
      const context = contexts.find((candidate) => candidate.grantId === grantId);
      setBrowseResults(
        context !== undefined && isGlobalAdministrator(context.roleCode)
          ? result.items.filter((item) =>
              !context.organizationScopeUnitIds.includes(item.unitId))
          : result.items,
      );
    } catch {
      setBrowseError(true);
      setBrowseResults(null);
    } finally {
      setBrowseLoading(false);
    }
  }

  function openBrowse(grantId: string): void {
    setBrowseGrantId(grantId);
    setBrowseQuery("");
    setBrowseResults(null);
    setBrowseError(false);
    void runBrowse(grantId, "");
  }

  function closeBrowse(): void {
    setBrowseGrantId(null);
    setBrowseResults(null);
  }

  function onBrowseQueryChange(event: ChangeEvent<HTMLInputElement>): void {
    const { value } = event.target;
    setBrowseQuery(value);
    if (browseGrantId !== null) void runBrowse(browseGrantId, value);
  }

  async function selectBrowsed(organizationUnitId: string): Promise<void> {
    if (browseGrantId === null) return;
    const grantId = browseGrantId;
    closeBrowse();
    await performSwitch(grantId, organizationUnitId);
  }

  const browsingContext = contexts.find((context) => context.grantId === browseGrantId) ?? null;

  return (
    <div className="session-controls">
      {!hideIdentity && session.identity !== null ? (
        <span className="session-identity" title={session.identity.displayName}>
          <span className="sr-only">{locale.text("signedInAs")}</span>
          <span aria-hidden="true" className="session-avatar">
            {session.identity.displayName.slice(0, 1).toUpperCase()}
          </span>
          <strong className="sr-only">{session.identity.displayName}</strong>
        </span>
      ) : null}

      {!hideIdentity && session.activeOrganizationUnit !== null ? (
        <span className="session-active-organization">
          <span className="sr-only">{locale.text("workContextActiveOrganizationLabel")}</span>
          {session.isGlobalAdministrationContext
            ? locale.code === "de" ? "Alle Organisationen" : "All organizations"
            : session.activeOrganizationUnit.name ?? (locale.code === "de" ? "Organisation nicht verfügbar" : "Organization unavailable")}
        </span>
      ) : null}

      {contexts.length === 0 ? (
        <span className="session-context-empty">{locale.text("workContextEmpty")}</span>
      ) : (
        <fieldset
          aria-describedby={noteId}
          className="session-context"
          disabled={session.contextSwitching}
        >
          <legend>{locale.text("workContextLabel")}</legend>
          <span className="sr-only" id={noteId}>
            {locale.text("workContextNarrowNote")}
          </span>
          <div className="session-context-options">
            {visibleOptions.map((option) => (
              <label className="session-context-option" key={option.key}>
                <input
                  aria-label={option.label}
                  checked={activeKey === option.key}
                  name={`session-context-${noteId}`}
                  onChange={() => void onSwitch(option.key)}
                  type="radio"
                  value={option.key}
                />
                <span className="session-context-option__text">
                  <strong>{option.organizationLabel}</strong>
                  <small>{workContextLabel(option.roleCode, locale.code)}</small>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {browsableContexts.map((context) => (
        <Button
          key={context.grantId}
          onClick={() => {
            openBrowse(context.grantId);
          }}
          variant="ghost"
        >
          {locale.text("workContextBrowseAction")} ({workContextLabel(context.roleCode, locale.code)})
        </Button>
      ))}

      <Button onClick={() => { setPasswordDialogOpen(true); }} variant="ghost">
        {locale.text("changePasswordAction")}
      </Button>

      <Button onClick={() => void onLogout()} variant="danger">
        {locale.text("logoutAction")}
      </Button>

      {session.activeContext === null && contexts.length > 0 ? (
        <span className="session-context-hint">{locale.text("workContextNone")}</span>
      ) : null}

      {error !== null ? (
        <p className="session-error" role="alert">
          {locale.text(error)}
        </p>
      ) : null}

      <Dialog
        className="session-context-picker"
        onClose={closeBrowse}
        open={browsingContext !== null}
        title={locale.text("workContextBrowseTitle")}
      >
        <label className="session-context-search">
          <span className="sr-only">{locale.text("workContextSearchPlaceholder")}</span>
          <TextInput onChange={onBrowseQueryChange} placeholder={locale.text("workContextSearchPlaceholder")} value={browseQuery} />
        </label>
        {browseLoading ? <p>{locale.text("workContextSearchLoading")}</p> : null}
        {browseError ? (
          <p role="alert">{locale.text("workContextSearchError")}</p>
        ) : null}
        {!browseLoading && !browseError && browseResults !== null && browseResults.length === 0 ? (
          <p>{locale.text("workContextSearchEmpty")}</p>
        ) : null}
        {!browseLoading && !browseError && browseResults !== null && browseResults.length > 0 ? (
          <ul className="session-context-search-results">
            {browseResults.map((item) => (
              <li key={item.unitId}>
                <Button onClick={() => void selectBrowsed(item.unitId)} variant="ghost">
                  {item.label}
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </Dialog>

      <Dialog
        onClose={() => {
          setPasswordDialogOpen(false);
          setPasswordError(null);
        }}
        open={passwordDialogOpen}
        title={locale.text("changePasswordTitle")}
      >
        <Form
          actions={
            <>
              <Button onClick={() => { setPasswordDialogOpen(false); }} variant="secondary">
                {locale.text("cancel")}
              </Button>
              <Button busy={passwordBusy} type="submit" variant="primary">
                {locale.text("changePasswordSubmit")}
              </Button>
            </>
          }
          label={locale.text("changePasswordTitle")}
          onSubmit={() => { void onChangePassword(); }}
        >
          <Field label={locale.text("changePasswordCurrent")} required>
            <TextInput
              autoComplete="current-password"
              maxLength={1024}
              onChange={(event) => { setCurrentPassword(event.target.value); }}
              required
              type="password"
              value={currentPassword}
            />
          </Field>
          <Field hint={locale.text("changePasswordHint")} label={locale.text("changePasswordNew")} required>
            <TextInput
              autoComplete="new-password"
              minLength={12}
              maxLength={1024}
              onChange={(event) => { setNewPassword(event.target.value); }}
              required
              type="password"
              value={newPassword}
            />
          </Field>
          <Field
            error={passwordError === null ? undefined : locale.text(passwordError)}
            label={locale.text("changePasswordConfirm")}
            required
          >
            <TextInput
              autoComplete="new-password"
              minLength={12}
              maxLength={1024}
              onChange={(event) => {
                setNewPasswordConfirmation(event.target.value);
                if (passwordError === "changePasswordMismatch") setPasswordError(null);
              }}
              required
              type="password"
              value={newPasswordConfirmation}
            />
          </Field>
        </Form>
      </Dialog>
    </div>
  );
});
